/**
 * CHA-466 — CSV builders for the Target Item Dims tab.
 *
 * Pure string-building, separated from the download plumbing so it can be tested. The browser
 * side (Blob, anchor, revokeObjectURL) stays in the component.
 *
 * Values are rounded to the precision actually UPLOADED — 2dp for the axes, 3dp for weight.
 * The resolver emits unrounded FLOAT64 (a 42x36x11 cm carton of 12 gives 0.36089238845144356),
 * and Target stores whatever it is sent, verbatim. Exporting the unrounded value would mean
 * pasting 0.36089 into Target, after which the drift check — which compares at upload
 * precision — reports the item as differing forever. That is the CHA-465 trap, reachable from
 * this direction too.
 */

/** Decimal places as sent to Target. */
export const AXIS_DP = 2;
export const WEIGHT_DP = 3;

/** Fixed-precision, or '' when the value is absent. Never renders the string "NaN". */
export function fixed(v, dp) {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(dp) : '';
}

/**
 * RFC-4180 field escaping: quote when the value contains a comma, quote or newline, and double
 * any embedded quotes. Style codes and descriptions can contain commas.
 */
export function escapeCsv(v) {
    const str = String(v ?? '');
    // \r matters as much as \n: a lone CR is a record boundary to some readers, so an unquoted
    // field containing one shifts every later row.
    return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/**
 * Force a spreadsheet to treat a digit run as TEXT.
 *
 * Excel and Sheets infer a number when a CSV is opened, so `0822826137964` becomes
 * `822826137964` and long runs can render in scientific notation. Either way the operator copies a
 * DIFFERENT identifier into Target and updates the wrong item — the failure is silent and lands on
 * the wrong record. `="…"` is the portable way to pin a text cell in both programs.
 *
 * Applied only to all-digit values, so there is no formula-injection surface: a digit run cannot be
 * a harmful formula, and anything non-numeric is passed through untouched.
 */
export function spreadsheetText(v) {
    const str = String(v ?? '');
    return /^\d+$/.test(str) ? `="${str}"` : str;
}

/** Joins a header row plus data rows into CSV text. */
export function toCsv(headers, rows) {
    return [headers, ...rows].map(row => row.map(escapeCsv).join(',')).join('\n');
}

/**
 * The values to send to Target.
 * @param {Array<object>} rows getTargetDimUploadList().upload
 */
export function buildDimsCsv(rows) {
    const headers = ['Style', 'UPC', 'Route', 'H', 'W', 'D', 'Weight', 'Weight UOM', 'Package Quantity', 'Unit', 'Cube (cu in)', 'Source'];
    const lines = (rows || []).map(r => [
        r.sty,
        // Pinned to a text cell — a bare digit run loses its leading zero on open, which would
        // point the Target update at a different item.
        spreadsheetText(r.upc),
        r.route,
        fixed(r.h_in, AXIS_DP),
        fixed(r.w_in, AXIS_DP),
        fixed(r.d_in, AXIS_DP),
        fixed(r.weight_lb, WEIGHT_DP),
        'POUND',
        r.packQty === null || r.packQty === undefined ? '' : r.packQty,
        r.unit || 'Each',
        fixed(r.cube_cuin, 1),
        r.source || 'matson',
    ]);
    return toCsv(headers, lines);
}

/**
 * The missing-dims worklist. Carries the REASON, because this file gets handed to someone to
 * chase and "waiting on Matson" needs a different action from "needs a manual measurement".
 *
 * @param {Array<object>} rows getTargetDimUploadList().missing
 * @param {object} reasonLabels reason code -> human label
 */
export function buildMissingCsv(rows, reasonLabels = {}) {
    const headers = ['Style', 'Colour', 'Size', 'Stage', 'Reason', 'Route', 'UPCs checked', 'UPCs missing', 'Partial'];
    const lines = (rows || []).map(r => [
        r.sty,
        r.clr || '',
        r.size_code || '',
        r.stage || '',
        (reasonLabels[r.reason] || {}).label || r.reason,
        r.route || '',
        r.upcsChecked === null || r.upcsChecked === undefined ? '' : r.upcsChecked,
        r.upcsMissing === null || r.upcsMissing === undefined ? '' : r.upcsMissing,
        r.partial ? 'yes' : '',
    ]);
    return toCsv(headers, lines);
}

/** `target-dims-ready_20260814-1830.csv` */
export function timestampedName(prefix, now = new Date()) {
    const pad = n => String(n).padStart(2, '0');
    const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    return `${prefix}_${ts}.csv`;
}
